<script lang="ts" module>
	import { holdsPiece } from '../till/notePieces';

	export interface ShortcutChip {
		words: string;
		on: boolean;
		testId: string;
	}

	// The note shortcuts as chips, in the store's order (UC-3.R8): each is on
	// while the note holds its words as a whole piece. A chip's test id is its
	// place, since the words are any text a cashier typed.
	export function shortcutChips(shortcuts: string[], note: string): ShortcutChip[] {
		return shortcuts.map((words, i) => ({
			words,
			on: holdsPiece(note, words),
			testId: `editor-shortcut-${i}`,
		}));
	}
</script>

<script lang="ts">
	// UC-3. The options an item offers, and the choices a cashier taps: the
	// body of the editor's sheet. The till draws the sheet, with the item's
	// name, its live unit price and Done in the header, and holds what is
	// typed here (till/editor.ts), so closing the sheet loses nothing.
	//
	// Answers and the note at the inline start, the quantity at the inline
	// end: a stepper for one more or one fewer, and the pad for a number.
	// A portion of an item sold by weight (#917) has no quantity: the pad at
	// the inline end takes its whole grams, which it asks first, and the
	// header's price is what those grams cost with the choices. A per-kg
	// choice (#935) adds its rate for those grams.

	import Button from '../components/Button.svelte';
	import Choice from '../components/Choice.svelte';
	import Field from '../components/Field.svelte';
	import Keypad from '../components/Keypad.svelte';
	import { tick } from 'svelte';
	import { message, type Language } from '../i18n/messages';
	import { formatMinor } from '../format/money';
	import { fitCount, NOTE_SHORTCUT_ROWS, togglePiece } from '../till/notePieces';
	import { MAX_GRAMS, MAX_QTY, MIN_QTY } from '../domain/pricing';
	import {
		gramsOk,
		groupsOf,
		perKgAmount,
		perKgGroup,
		unitOf,
		weighed,
		type EditorWork,
	} from '../till/editor';
	import { perKgRate } from '../till/weighed';
	import type { components } from '../api/types';

	type Menu = components['schemas']['MenuBody'];

	interface Props {
		language: Language;
		menu: Menu;
		minorDigits: number;
		work: EditorWork;
		/** The item's note shortcuts, most used first; empty while the store has not answered. */
		shortcuts?: string[];
		onChange: (work: EditorWork) => void;
	}

	let { language, menu, minorDigits, work, shortcuts = [], onChange }: Props = $props();

	const groups = $derived(groupsOf(menu, work.itemId));
	const belowZero = $derived(unitOf(menu, work) < 0);
	const byWeight = $derived(weighed(work));

	// The note shortcuts this till learned for the item (UC-3.R8, T6). A tap
	// adds the words to the note as one piece, a second tap takes that piece
	// out again, so a shortcut works like a switch the cashier can see. As
	// many as fit in two rows show, the first ones of the list: a chip that
	// does not fit hides, and so does every chip after it.
	const chips = $derived(shortcutChips(shortcuts, work.note));
	let row = $state<HTMLDivElement>();
	let shown = $state(Number.POSITIVE_INFINITY);

	// Every chip is laid out, then each one's row is read, and the chips past
	// the last row hide: on a new list, and when the row's width changes. Only
	// the latest fit measures: a hidden chip has no row, so a fit that read the
	// chips another fit had already hidden would count them all as fitting.
	let fits = 0;
	async function fit() {
		const mine = ++fits;
		shown = Number.POSITIVE_INFINITY;
		await tick();
		if (!row || mine !== fits) return;
		shown = fitCount(
			Array.from(row.children, (chip) => (chip as HTMLElement).offsetTop),
			NOTE_SHORTCUT_ROWS,
		);
	}

	$effect(() => {
		const el = row;
		const list = shortcuts.join('\n');
		if (!el || list === '') return;
		let width = el.clientWidth;
		void fit();
		const resized = new ResizeObserver(() => {
			if (el.clientWidth === width) return;
			width = el.clientWidth;
			void fit();
		});
		resized.observe(el);
		// A web font that loads after the first measure changes each chip's
		// width, though neither the list nor the row's width changed.
		let open = true;
		void document.fonts?.ready.then(() => {
			if (open) void fit();
		});
		return () => {
			open = false;
			resized.disconnect();
		};
	});

	function change(patch: Partial<EditorWork>) {
		onChange({ ...work, ...patch });
	}

	function idsIn(groupId: string): string[] {
		return (groups.find((g) => g.id === groupId)?.modifiers ?? []).map((m) => m.id);
	}

	function pickedIn(groupId: string): string[] {
		const inGroup = idsIn(groupId);
		return work.chosen.filter((id) => inGroup.includes(id));
	}

	// Choice hands back the group's whole answer; the item's other answers
	// stay as they were.
	function answer(groupId: string, picked: string | string[]) {
		const inGroup = idsIn(groupId);
		const next = Array.isArray(picked) ? picked : [picked];
		change({ chosen: [...work.chosen.filter((id) => !inGroup.includes(id)), ...next] });
	}

	// An option chosen exactly once is a radio row: a tap swaps, as a
	// cashier expects from Small and Large. Anything else is checkboxes,
	// because an optional answer must be able to go.
	function modeOf(group: { minSelect: number; maxSelect: number }): 'single' | 'multi' {
		return group.minSelect === 1 && group.maxSelect === 1 ? 'single' : 'multi';
	}

	function ruleOf(group: { minSelect: number; maxSelect: number }): string | undefined {
		if (group.minSelect < 1) return undefined;
		return modeOf(group) === 'single'
			? message(language, 'editorChooseOne')
			: `${message(language, 'editorChooseAtLeast')} ${group.minSelect}`;
	}

	// Only what costs more or less shows a price, and a change carries its sign.
	function deltaOf(minor: number): string | undefined {
		if (minor === 0) return undefined;
		return minor > 0 ? `+${formatMinor(minor, minorDigits)}` : formatMinor(minor, minorDigits);
	}

	// A per-kg choice (UC-3.R11) reads its rate, "+50.00 / كجم"; once the
	// portion has its grams, its amount for them, "+12.50", is its price and
	// the rate moves under its name, both live as the grams change.
	function optionOf(group: { kind?: string | null }, modifier: { priceDeltaMinor: number }) {
		if (!perKgGroup(group)) return { price: deltaOf(modifier.priceDeltaMinor) };
		const rate = perKgRate(language, modifier.priceDeltaMinor, minorDigits);
		if (!gramsOk(work)) return { price: rate };
		return { price: deltaOf(perKgAmount(modifier.priceDeltaMinor, work.grams)), sub: rate };
	}

	function toggleShortcut(words: string) {
		change({ note: togglePiece(work.note, words) });
	}

	// A quantity that was just set, by opening the editor or by the stepper,
	// reads as an empty pad: the first key replaces it rather than making 12
	// out of a tap on 2. A fourth digit is refused.
	function typeQty(key: string) {
		const current = work.qtyFresh ? '' : String(work.qty);
		const next = (current + key).replace(/^0+/, '');
		if (next === '' || next.length > 3) return;
		change({ qty: Number(next), qtyFresh: false });
	}

	// Whole grams, typed digit by digit; a digit past MAX_GRAMS is refused,
	// and a leading zero is no digit.
	function typeGrams(key: string) {
		const next = Number(`${work.grams || ''}${key}`);
		if (!Number.isSafeInteger(next) || next === 0 || next > MAX_GRAMS) return;
		change({ grams: next });
	}

	function backGrams() {
		change({ grams: Math.floor((work.grams ?? 0) / 10) });
	}

	function backQty() {
		const rest = String(work.qty).slice(0, -1);
		change({ qty: Math.max(MIN_QTY, Number(rest) || MIN_QTY), qtyFresh: rest === '' });
	}
</script>

<div class="editor" data-testid="panel-line-editor">
	{#if belowZero}
		<p class="alert" role="alert" data-testid="editor-below-zero">
			{message(language, 'editorBelowZero')}
		</p>
	{/if}
	<div class="body">
		<div class="answers" data-scroll="">
			{#each groups as group (group.id)}
				{@const mode = modeOf(group)}
				<Choice
					testId="editor-group-{group.id}"
					title={group.name}
					rule={ruleOf(group)}
					ruleTestId="editor-need-{group.id}"
					{mode}
					min={group.minSelect}
					max={group.maxSelect}
					value={mode === 'single' ? (pickedIn(group.id)[0] ?? '') : pickedIn(group.id)}
					onChange={(picked) => answer(group.id, picked)}
					soldOutLabel={message(language, 'sellSoldOut')}
					options={(group.modifiers ?? []).map((modifier) => ({
						value: modifier.id,
						label: modifier.name,
						...optionOf(group, modifier),
						soldOut: !modifier.available,
						testId: `choice-${modifier.id}`,
					}))}
				/>
			{/each}
			<div class="note">
				<Field
					testId="editor-note"
					label={message(language, 'editorNote')}
					value={work.note}
					maxLength={200}
					onInput={(value) => change({ note: value })}
				/>
				{#if chips.length > 0}
					<div class="shortcuts" bind:this={row} data-testid="editor-shortcuts">
						{#each chips as chip, i (chip.words)}
							<div class="chip" hidden={i >= shown}>
								<Button
									testId={chip.testId}
									label={chip.words}
									tone={chip.on ? 'ink' : 'secondary'}
									size="sm"
									truncate
									pressed={chip.on}
									onPress={() => toggleShortcut(chip.words)}
								/>
							</div>
						{/each}
					</div>
				{/if}
			</div>
		</div>
		{#if byWeight}
			<section class="qty">
				<h3>{message(language, 'editorGrams')}</h3>
				<output class="count" dir="ltr" data-testid="editor-grams"
					>{work.grams || ''} <span class="unit">{message(language, 'unitGrams')}</span></output
				>
				<Keypad
					testId="editor-qty-pad"
					label={message(language, 'editorGrams')}
					deleteLabel={message(language, 'backspace')}
					onKey={typeGrams}
					onBackspace={backGrams}
					canDelete={(work.grams ?? 0) > 0}
				/>
			</section>
		{:else}
			<section class="qty">
				<h3>{message(language, 'sellQty')}</h3>
				<div class="stepper" dir="ltr">
					<Button
						testId="editor-qty-down"
						label=""
						ariaLabel={message(language, 'editorFewer')}
						tone="secondary"
						size="md"
						icon="minus"
						disabled={work.qty <= MIN_QTY}
						onPress={() => change({ qty: Math.max(MIN_QTY, work.qty - 1), qtyFresh: true })}
					/>
					<output class="count" data-testid="editor-qty">{work.qty}</output>
					<Button
						testId="editor-qty-up"
						label=""
						ariaLabel={message(language, 'editorMore')}
						tone="secondary"
						size="md"
						icon="plus"
						disabled={work.qty >= MAX_QTY}
						onPress={() => change({ qty: Math.min(MAX_QTY, work.qty + 1), qtyFresh: true })}
					/>
				</div>
				<Keypad
					testId="editor-qty-pad"
					label={message(language, 'sellQty')}
					deleteLabel={message(language, 'backspace')}
					onKey={typeQty}
					onBackspace={backQty}
				/>
			</section>
		{/if}
	</div>
</div>

<style>
	.editor {
		flex: 1;
		min-block-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
	}

	.alert {
		margin: 0;
		font-size: var(--text-sm);
		font-weight: 600;
		color: var(--ink-flag-deep);
	}

	.body {
		flex: 1;
		min-block-size: 0;
		display: flex;
		gap: var(--space-9);
	}

	.answers {
		flex: 1;
		min-inline-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.note {
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
	}

	.shortcuts {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-3);
	}

	/* A chip wider than its row is cut at its end (Button's truncate). */
	.chip {
		min-inline-size: 0;
		max-inline-size: 100%;
	}

	.chip[hidden] {
		display: none;
	}

	.qty {
		flex: none;
		min-block-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	h3 {
		margin: 0;
		font-size: var(--text-sm);
		font-weight: 400;
		color: var(--text-muted);
	}

	.stepper {
		display: grid;
		grid-template-columns: var(--key-w) 1fr var(--key-w);
		gap: var(--touch-gap);
		align-items: center;
	}

	.unit {
		font-size: var(--text-sm);
		font-weight: 400;
		color: var(--text-muted);
	}

	.count {
		font-family: var(--font-mono);
		font-size: var(--figure-sm);
		font-weight: 700;
		text-align: center;
	}
</style>
